import { Action } from 'redux';
import { filter, mergeMap } from 'rxjs/operators';

import { selectAllFeatureFlags } from '../../../api/features';
import { push, replace } from '../../../app/reducers';
import { pageValidationSchema } from '../../../config/page-validation-schema';
import { QualificationAction, QualificationStepResponse } from '../../../domain/qualification';
import { decodeApplicationFinalStatus } from '../../../domain/registration-errors';
import {
  APPLICATION_UNDER_REVIEW,
  COUNTER_OFFER,
  CREDIT_LOCKED,
  DOCUMENTS,
  FETCHING_BANK_DATA,
  FINANCIAL_STATEMENTS,
  PERSONA,
  PERSONAL_INFO,
  PREQUALIFIED,
  PREVIEW_CARD,
  PRE_COLLECT_UBOS,
  REVIEW_APPLICATION,
  VERIFY_REVENUE,
  VERIFY_REVENUE_DOCS,
} from '../../../paths';
import { RootState } from '../../../store';
import { RootEpic } from '../../../store/epics';
import { continueApplicationProcessing } from '../reducers/application';
import { selectCreditTerms } from '../reducers/credit-terms.reducer';

const finalStatusActions = (response: QualificationStepResponse, state: RootState): Action[] =>
  decodeApplicationFinalStatus(response.finalStatus, state);

const navTo = (path: string, state: RootState): Action => {
  const { selectedOfferId } = selectCreditTerms(state);
  return selectedOfferId ? replace(path) : push(path);
};

const actionsByAction: Record<
  QualificationAction,
  (response: QualificationStepResponse, state: RootState) => Action[]
> = {
  select_offer: (_response, state) => [navTo(PREQUALIFIED, state)],
  get_ubos: (_response, state) => [navTo(REVIEW_APPLICATION, state)],
  pre_collect_ubos: (_response, state) => [navTo(PRE_COLLECT_UBOS, state)],
  verify_ubos: (_response, state) => [navTo(REVIEW_APPLICATION, state)],
  counter_offer: (_response, state) => [navTo(COUNTER_OFFER, state)],
  collect_business_and_applicant_info: (_response, state) => [navTo(findNextPersonalOrBusinessPage(state), state)],
  persona: (_response, state) => [navTo(PERSONA, state)],
  preview_card_order: (_response, state) => [navTo(PREVIEW_CARD, state)],
  manual_review: (_response, state) => [navTo(APPLICATION_UNDER_REVIEW, state)],
  collect_documents: (_response, state) => [navTo(DOCUMENTS, state)],
  collect_financials: (_response, state) => [navTo(FINANCIAL_STATEMENTS, state)],
  unlock_consumer_credit: (_response, state) => [navTo(CREDIT_LOCKED, state)],
  verify_revenue: (_response, state) => [navTo(getNextVerifyRevenuePage(state), state)],
  verify_fetching_revenue: (_response, state) => [navTo(FETCHING_BANK_DATA, state)],
  verification_review: (_response, state) => [navTo(APPLICATION_UNDER_REVIEW, state)],
  declined: finalStatusActions,
  approved: finalStatusActions,
  verified: finalStatusActions,
};

const continueApplicationProcessingEpic: RootEpic = (action$, $state) => {
  return action$.pipe(
    filter(continueApplicationProcessing.match),
    mergeMap(({ payload: { redirect, response } }) =>
      redirect ? actionsByAction[response.action](response, $state.value) : [],
    ),
  );
};

const findNextPersonalOrBusinessPage = (state: RootState): string => {
  return pageValidationSchema.find(({ validate }) => !validate(state))?.route || PERSONAL_INFO;
};

const getNextVerifyRevenuePage = (state: RootState): string => {
  const featureFlags = selectAllFeatureFlags(state);
  if (featureFlags?.enableLinkBankAccount) {
    return VERIFY_REVENUE;
  }

  return VERIFY_REVENUE_DOCS;
};

export { continueApplicationProcessingEpic };
